/**
 * Wind/weather at own position (spec 11) — MET Norway via the API proxy.
 * Fetch triggers: mount with a fix, then re-fetch when moved > 5 km or the
 * last reading is older than 30 min. Last response persisted to IDB so an
 * offline session still shows a stale-but-honest reading.
 */
import { create } from 'zustand';
import type { WeatherResponse } from '@boatnav/schemas';
import { haversineMeters } from '@boatnav/core';
import { getWeatherRecord, putWeatherRecord, type WeatherRecord } from '../../lib/db.js';

const REFETCH_DISTANCE_M = 5_000;
const REFETCH_AGE_MS = 30 * 60_000;

/** Age thresholds for the sheet's honesty rules (spec 11): dim after 2 h,
 *  hide (treat as if there were no data at all) after 12 h. */
export const WEATHER_DIM_AGE_MS = 2 * 3_600_000;
export const WEATHER_HIDE_AGE_MS = 12 * 3_600_000;

interface WeatherState {
  data: WeatherResponse | null;
  fetchedAt: number | null;
  atLat: number | null;
  atLon: number | null;
  loading: boolean;
  /** Load the last persisted reading from IDB (call once at app start). */
  init: () => Promise<void>;
  /** Fetch only if moved far enough or the data is stale; no-op otherwise. */
  maybeFetch: (lat: number, lon: number) => void;
  fetchFor: (lat: number, lon: number) => Promise<void>;
}

export const useWeatherStore = create<WeatherState>((set, get) => ({
  data: null,
  fetchedAt: null,
  atLat: null,
  atLon: null,
  loading: false,

  init: async () => {
    const rec = await getWeatherRecord().catch(() => undefined);
    if (rec) set({ data: rec.data, fetchedAt: rec.fetchedAt, atLat: rec.atLat, atLon: rec.atLon });
  },

  maybeFetch: (lat, lon) => {
    const { fetchedAt, atLat, atLon, loading } = get();
    if (loading) return;
    const now = Date.now();
    const stale = fetchedAt == null || now - fetchedAt > REFETCH_AGE_MS;
    const moved =
      atLat == null ||
      atLon == null ||
      haversineMeters({ lat, lon }, { lat: atLat, lon: atLon }) > REFETCH_DISTANCE_M;
    if (stale || moved) void get().fetchFor(lat, lon);
  },

  fetchFor: async (lat, lon) => {
    set({ loading: true });
    try {
      const res = await fetch(`/api/weather?lat=${lat}&lon=${lon}`);
      if (!res.ok) return; // keep whatever stale reading is already shown
      const data = (await res.json()) as WeatherResponse;
      const fetchedAt = Date.now();
      set({ data, fetchedAt, atLat: lat, atLon: lon });
      const rec: Omit<WeatherRecord, 'id'> = { data, fetchedAt, atLat: lat, atLon: lon };
      await putWeatherRecord(rec).catch(() => undefined);
    } catch {
      /* offline — keep the last persisted reading */
    } finally {
      set({ loading: false });
    }
  },
}));
